import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatMoney, gregorianToJalali } from "../lib/format";

interface Employee {
  id: string;
  name: string;
  role: string;
  salaryMinor: number;
  active: boolean;
}

interface PayrollRun {
  id: string;
  period: string;
  totalNetMinor: number;
  status: string;
  lines: { name: string; netMinor: number }[];
}

function currentJalaliPeriod(): string {
  const now = new Date();
  const { jy, jm } = gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return `${jy}-${String(jm).padStart(2, "0")}`;
}

export function HrPage() {
  const { orgId } = useSession();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [salaryAfn, setSalaryAfn] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const unsubEmployees = onSnapshot(
      query(collection(db, "organizations", orgId, "employees"), orderBy("name")),
      (snap) =>
        setEmployees(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Employee, "id">) })),
        ),
    );
    const unsubRuns = onSnapshot(
      query(collection(db, "organizations", orgId, "payrollRuns"), orderBy("period", "desc")),
      (snap) =>
        setRuns(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PayrollRun, "id">) }))),
    );
    return () => {
      unsubEmployees();
      unsubRuns();
    };
  }, [orgId]);

  async function addEmployee() {
    setBusy(true);
    try {
      await api(`/orgs/${orgId}/hr/employees`, {
        method: "POST",
        body: {
          name,
          role,
          salaryMinor: Math.round(Number(salaryAfn) * 100),
          salaryCurrency: "AFN",
          hireDate: new Date().toISOString().slice(0, 10),
        },
      });
      setToast("کارمند ثبت شد");
      setAddOpen(false);
      setName("");
      setRole("");
      setSalaryAfn("");
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  }

  async function attendance(employeeId: string) {
    try {
      const result = await api<{ state: string }>(`/orgs/${orgId}/hr/attendance/check`, {
        method: "POST",
        body: { employeeId },
      });
      setToast(result.state === "checked_in" ? "حاضری ورود ثبت شد" : "خروج ثبت شد");
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    }
  }

  async function createRun() {
    setBusy(true);
    try {
      const result = await api<{ lineCount: number; totalNetMinor: number }>(
        `/orgs/${orgId}/hr/payroll/runs`,
        { method: "POST", body: { period: currentJalaliPeriod() } },
      );
      setToast(
        `لیست معاش ${currentJalaliPeriod()} ساخته شد — ${result.lineCount} کارمند، مجموع ${formatMoney(result.totalNetMinor)}`,
      );
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  }

  async function postRun(runId: string) {
    setBusy(true);
    try {
      await api(`/orgs/${orgId}/hr/payroll/runs/${runId}/post`, { method: "POST" });
      setToast("معاش پرداخت و به دفاتر ثبت شد (مصرف معاشات / صندوق)");
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          کارمندان و معاشات
        </Typography>
        <Button variant="outlined" onClick={() => setAddOpen(true)}>
          + کارمند جدید
        </Button>
        <Button variant="contained" onClick={createRun} disabled={busy || employees.length === 0}>
          ساخت لیست معاش {currentJalaliPeriod()}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>نام</TableCell>
              <TableCell>وظیفه</TableCell>
              <TableCell>معاش ماهانه</TableCell>
              <TableCell>حاضری امروز</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => (
              <TableRow key={employee.id} hover>
                <TableCell>{employee.name}</TableCell>
                <TableCell>{employee.role}</TableCell>
                <TableCell>{formatMoney(employee.salaryMinor)}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => attendance(employee.id)}>
                    ثبت ورود/خروج
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    هنوز کارمندی ثبت نشده
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" mb={1}>
        لیست‌های معاش
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>دوره (شمسی)</TableCell>
              <TableCell>کارمندان</TableCell>
              <TableCell>مجموع خالص</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.id} hover>
                <TableCell>{run.period}</TableCell>
                <TableCell>{run.lines?.length ?? 0}</TableCell>
                <TableCell>{formatMoney(run.totalNetMinor)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={run.status === "posted" ? "success" : "warning"}
                    label={run.status === "posted" ? "پرداخت شده" : "پیش‌نویس"}
                  />
                </TableCell>
                <TableCell align="left">
                  {run.status === "draft" && (
                    <Button size="small" variant="contained" disabled={busy} onClick={() => postRun(run.id)}>
                      پرداخت و ثبت در دفاتر
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    لیست معاشی ساخته نشده
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>کارمند جدید</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="نام کامل" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <TextField label="وظیفه" value={role} onChange={(e) => setRole(e.target.value)} />
            <TextField
              label="معاش ماهانه (افغانی)"
              type="number"
              value={salaryAfn}
              onChange={(e) => setSalaryAfn(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            onClick={addEmployee}
            disabled={busy || name.trim().length < 2 || role.trim().length < 2 || Number(salaryAfn) <= 0}
          >
            ثبت
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} severity="info">
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}
